// TODO: Polygons and lines are "disappearing" after switching to satellite
// This is due to how we are ordering the layers directly below the `firstSymbolLayerId`
//  and the fact that in Mapbox Studio, we have satellite on top of everything.

import React from 'react';
import PropTypes from 'prop-types';
import { flattenDeep, isEqual, merge } from 'lodash';
import { MapFullscreenControlv2, MapLayerControl, NoMapboxGlNotice, announce, MapGestureHandler } from 'sarsaparilla';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

const propTypes = {
  accessToken: PropTypes.string,
  center: PropTypes.array,
  fullscreenControl: PropTypes.bool,
  // A GeoJSON Feature object
  geoJsonCenterPoint: PropTypes.objectOf(PropTypes.any),
  // A GeoJSON FeatureCollection object
  geoJsonLines: PropTypes.objectOf(PropTypes.any),
  // A GeoJSON FeatureCollection object
  geoJsonPoints: PropTypes.objectOf(PropTypes.any),
  // A GeoJSON FeatureCollection object
  geoJsonPolygons: PropTypes.objectOf(PropTypes.any),
  handleClickCallback: PropTypes.func,
  mapContainer: PropTypes.string.isRequired,
  mapboxStyle: PropTypes.string,
  selectedFeatureId: PropTypes.string,
  showPolygonLabels: PropTypes.bool,
  zoom: PropTypes.number
};
const defaultProps = {
  accessToken: process.env.MAPBOX_ACCESS_TOKEN,
  // Default to center of the CONUS
  center: [
    -96,
    39
  ],
  fullscreenControl: true,
  showPolygonLabels: false,
  mapboxStyle: process.env.MAPBOX_STYLE,
  zoom: 13
};

export default class PermitMap extends React.Component {
  componentDidMount() {
    this.permitMapPage = document.querySelector('div.map') || document.querySelector('div.permit-division-map');
    if (this.permitMapPage) {
      const obsserverCallback = (elements) => {
        if (elements[0] && elements[0].isIntersecting && !this.didMapLoad && !this.map) {
          this.mountMap();
        }
      };

      this.observer = new IntersectionObserver(obsserverCallback, { rootMargin: '100px' });
      this.observer.observe(this.permitMapPage);
    } else if (!this.map) {
      this.mountMap();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.map) {
      return;
    }

    let addCenterPoint = typeof nextProps.geoJsonCenterPoint !== 'undefined' && nextProps.geoJsonCenterPoint !== null;
    let dataChanged = false;
    let source;

    if (!isEqual(this.props.geoJsonLines, nextProps.geoJsonLines)) {
      source = this.map.getSource('linesSource');

      if (source) {
        addCenterPoint = false;
        dataChanged = true;
        source.setData(nextProps.geoJsonLines ? nextProps.geoJsonLines : {
          features: [],
          type: 'FeatureCollection'
        });
      }
    }

    if (!isEqual(this.props.geoJsonPoints, nextProps.geoJsonPoints)) {
      source = this.map.getSource('pointsSource');

      if (source) {
        addCenterPoint = false;
        dataChanged = true;
        source.setData(nextProps.geoJsonPoints ? nextProps.geoJsonPoints : {
          features: [],
          type: 'FeatureCollection'
        });
      }
    }

    if (!isEqual(this.props.geoJsonPolygons, nextProps.geoJsonPolygons)) {
      source = this.map.getSource('polygonsSource');

      if (source) {
        addCenterPoint = false;
        dataChanged = true;
        source.setData(nextProps.geoJsonPolygons ? nextProps.geoJsonPolygons : {
          features: [],
          type: 'FeatureCollection'
        });
      }
    }

    source = this.map.getSource('permitSource');

    if (source) {
      // TODO: Detect if this data has changed and set dataChanged to true

      if (addCenterPoint) {
        source.setData({
          features: [
            nextProps.geoJsonCenterPoint
          ],
          type: 'FeatureCollection'
        });
      }
      else {
        source.setData({
          features: [],
          type: 'FeatureCollection'
        });
      }
    }

    if (dataChanged) {
      this.checkAndSetBounds(nextProps);
    }

    if (!isEqual(this.props.selectedFeatureId, nextProps.selectedFeatureId)) {
      if (nextProps.selectedFeatureId) {
        const foundFeature = this.findFeature(nextProps.selectedFeatureId);

        if (foundFeature) {
          let coordinates = null;
          // let boundingbox = null;
          const me = this;

          switch (foundFeature.geometry.type) {
            case 'Point':
              coordinates = foundFeature.geometry.coordinates;

              // Pan and/or zoom the map to the selected feature
              me.map.flyTo({
                center: coordinates,
                duration: 1200,
                zoom: 17
              });
              // After animation, show popup
              setTimeout(() => {
                // TODO: Also show selected marker
                // TODO: Switch to setInterval and show popup when map has stopped panning/zooming
                me.showPopup(null, coordinates, foundFeature.properties.name);
              }, 1200);

              break;
            default:
              coordinates = turf.centroid(foundFeature).geometry.coordinates;
              const bbox = turf.bbox(foundFeature);

              const bounds = new me.mapboxgl.LngLatBounds(
                new me.mapboxgl.LngLat(bbox[0], bbox[1]),
                new me.mapboxgl.LngLat(bbox[2], bbox[3])
              );
              me.map.fitBounds(bounds, {
                duration: 1200,
                maxZoom: 16,
                padding: 100
              });
              // We aren't showing the popups for non-points because the placement of the center is not great.
          }
        }
      }
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
    }
  }
  
  mountMap() {
    if (mapboxgl.supported()) {
      let clicks = 0;

      this.mapboxgl = mapboxgl;
      this.mapboxgl.accessToken = this.props.accessToken;
      this.map = new mapboxgl.Map({
        center: this.props.geoJsonCenterPoint ? this.props.geoJsonCenterPoint.geometry.coordinates : this.props.center,
        container: this.props.mapContainer,
        style: this.props.mapboxStyle,
        zoom: this.props.zoom
      });
      this.popup = null;

      this.navigationControl = new this.mapboxgl.NavigationControl();
      this.mapFullscreenControl = new MapFullscreenControlv2();
      this.mapGestureControl = new MapGestureHandler({ fullscreenControl: this.mapFullscreenControl });

      this.map
        .addControl(this.navigationControl, 'top-left')
        .addControl(new MapLayerControl())
        .addControl(new this.mapboxgl.ScaleControl({
          maxWidth: 150,
          unit: 'imperial'
        }))
        .addControl(this.mapGestureControl)
        .on('click', (e) => {
          let lngLat = e.lngLat;
          const point = e.point;

          if(this.props?.handleClickCallback) {
            this.props.handleClickCallback(e.lngLat)
          }

          clicks = 0;

          setTimeout(() => {
            if (clicks === 0) {
              // Check for point first then lines then polygons
              let features = this.map.queryRenderedFeatures(point, {
                layers: [
                  'pointsLayer'
                ]
              });
              let feature;

              if (features && features.length) {
                feature = features[0];
                lngLat = null; // Set to null to display popup on marker
              }

              if (!feature) {
                features = this.map.queryRenderedFeatures(point, {
                  layers: [
                    'linesLayer'
                  ]
                });

                if (features && features.length) {
                  feature = features[0];
                }
              }

              if (!feature) {
                features = this.map.queryRenderedFeatures(point, {
                  layers: [
                    'polygonsLayer'
                  ]
                });

                if (features && features.length) {
                  feature = features[0];
                }
              }

              if (feature) {
                this.showPopup(lngLat, feature.geometry.coordinates, feature.properties.name);
              }
            }
          }, 250);
        })
        .on('dblclick', () => {
          clicks += 1;
        })
        .on('load', () => {
          this.didMapLoad = true;
          let centerSource = null;
          // TODO: Temporarily remove this to get Satellite working.
          //  DOWNSIDE: Our point/line/polygon data will appear on top of map labels
          /*
          // const layers = this.map.getStyle().layers;
          // let firstSymbolLayerId;
          //
          // for (let i = 0; i < layers.length; ++i) {
          //   const layer = layers[i];
          //
          //   if (layer.type === 'symbol') {
          //     firstSymbolLayerId = layer.id;
          //     break;
          //   }
          // }
          */

          // Add lines first, then polygons underneath them, then points...
          this.map
            .addSource('linesSource', {
              data: {
                features: [],
                type: 'FeatureCollection'
              },
              type: 'geojson'
            })
            .addLayer({
              id: 'linesLayer',
              layout: {
                'line-cap': 'round',
                'line-join': 'round'
              },
              paint: {
                'line-color': {
                  property: 'color',
                  type: 'identity'
                },
                'line-width': 8
              },
              source: 'linesSource',
              type: 'line'
            })
            // }, firstSymbolLayerId) // TODO: Temporarily remove this to get Satellite working.
            .addSource('polygonsSource', {
              data: {
                features: [],
                type: 'FeatureCollection'
              },
              type: 'geojson'
            })
            .addLayer({
              id: 'polygonsLinesLayer',
              layout: {
                'line-cap': 'round',
                'line-join': 'round'
              },
              paint: {
                'line-color': '#1c1d1f',
                'line-width': 1.2
              },
              source: 'polygonsSource',
              type: 'line'
            }, 'linesLayer')
            .addLayer({
              id: 'polygonsLayer',
              layout: {},
              paint: {
                'fill-color': {
                  property: 'color',
                  type: 'identity'
                },
                'fill-opacity': {
                  property: 'fillOpacity',
                  type: 'identity'
                },
                'fill-outline-color': '#2a2922'
              },
              source: 'polygonsSource',
              type: 'fill'
            }, 'polygonsLinesLayer')
            .addSource('centerSource', {
              data: {
                features: [],
                type: 'FeatureCollection'
              },
              type: 'geojson'
            })
            .addLayer({
              id: 'centerLayer',
              layout: {
                'icon-allow-overlap': true,
                // Pull icon from 'type' property; default to permit_pin if not specified
                'icon-image': [
                  'coalesce',
                  ['get', 'symbol'],
                  'permit_pin'
                ],
                'icon-offset': [0, -50/2]
              },
              source: 'centerSource',
              type: 'symbol'
            })
            .addSource('pointsSource', {
              data: {
                features: [],
                type: 'FeatureCollection'
              },
              type: 'geojson'
            })
            .addLayer({
              id: 'pointsLayer',
              layout: {
                'icon-allow-overlap': true,
                'icon-image': '{symbol}',
                'icon-offset': [0, -50/2]
              },
              source: 'pointsSource',
              type: 'symbol'
            });

          if (this.props.showPolygonLabels) {
            this.map
              .addLayer({
                id: 'polygonsLabelLayer',
                layout: {
                  'text-field': ['get', 'name'],
                  'text-padding': 10
                },
                paint: {
                  'text-halo-color': 'rgba(255, 255, 255, 0.75)',
                  'text-halo-width': 2,
                  'text-halo-blur': 1
                },
                source: 'polygonsSource',
                type: 'symbol'
              });
          }

          this.map
            // Hook up after layers have been added to map
            .on('mousemove', (e) => {
              let cursor = '';
              const features = this.map.queryRenderedFeatures(e.point, {
                layers: [
                  'linesLayer',
                  'pointsLayer',
                  'polygonsLayer'
                ]
              });

              if (features.length) {
                cursor = 'pointer';
              }

              this.map.getCanvas().style.cursor = cursor;
            });

          centerSource = this.map.getSource('centerSource');

          if (this.props.geoJsonLines || this.props.geoJsonPoints || this.props.geoJsonPolygons) {
            this.checkAndSetBounds(this.props);

            if (this.props.geoJsonLines) {
              this.map.getSource('linesSource').setData(this.processLines(this.props.geoJsonLines));
            }

            if (this.props.geoJsonPoints) {
              this.map.getSource('pointsSource').setData(this.processPoints(this.props.geoJsonPoints));
            }

            if (this.props.geoJsonPolygons) {
              this.map.getSource('polygonsSource').setData(this.processPolygons(this.props.geoJsonPolygons));
            }

            centerSource.setData({
              features: [],
              type: 'FeatureCollection'
            });
          }
          else if (this.props.geoJsonCenterPoint) {
            centerSource.setData({
              features: [
                this.props.geoJsonCenterPoint // TODO: Pass to this.processPoints
              ],
              type: 'FeatureCollection'
            });
          }
          this.navigationControl._zoomInButton.onclick = () => {
            announce('Zoom in button selected. You have zoomed in the map');
          };
          this.navigationControl._zoomOutButton.onclick = () => {
            announce('Zoom out button selected. You have zoomed out the map');
          };
          this.navigationControl._compass.onclick = () => {
            announce('Compass button selected. Map has returned to the default north orientation')
          }
        })
        .on('resize', () => {
          this.checkPopupAndRepositionMap();
        })
        .scrollZoom.disable();

      if (this.props.fullscreenControl) {
        this.map.addControl(this.mapFullscreenControl);
      }
    }
  }

  checkAndSetBounds(properties) {
    if (properties.geoJsonLines || properties.geoJsonPoints || properties.geoJsonPolygons) {
      let add = [];
      const bounds = new mapboxgl.LngLatBounds();
      let coordinates = [];

      if (properties.geoJsonLines) {
        properties.geoJsonLines.features.forEach((feature) => {
          coordinates = merge(coordinates, flattenDeep(feature.geometry.coordinates));
        });
      }

      if (properties.geoJsonPoints) {
        properties.geoJsonPoints.features.forEach((feature) => {
          bounds.extend(feature.geometry.coordinates);
        });
      }

      if (properties.geoJsonPolygons) {
        properties.geoJsonPolygons.features.forEach((feature) => {
          coordinates = merge(coordinates, flattenDeep(feature.geometry.coordinates));
        });
      }

      coordinates.forEach((coordinate) => {
        add.push(coordinate);

        if (add.length === 2) {
          bounds.extend(add);
          add = [];
        }
      });
      this.map.fitBounds(bounds, {
        duration: 0,
        maxZoom: 16,
        padding: 100
      });
    }
  }

  checkPopupAndRepositionMap() {
    const mapDiv = document.getElementsByClassName('mapboxgl-map')[0];
    const popupDiv = document.getElementsByClassName('mapboxgl-popup')[0];

    if (mapDiv && popupDiv) {
      const mapBounds = mapDiv.getBoundingClientRect();
      let offsetX = 0;
      let offsetY = 0;
      const popupBounds = popupDiv.getBoundingClientRect();

      if (popupBounds.bottom > mapBounds.bottom) {
        offsetY = popupBounds.bottom - mapBounds.bottom + 10;
      }
      else if (popupBounds.top < mapBounds.top) {
        offsetY = -(mapBounds.top - popupBounds.top) - 10;
      }

      if (popupBounds.left < mapBounds.left) {
        offsetX = -(mapBounds.left - popupBounds.left) - 10;
      }
      else if (popupBounds.right > mapBounds.right) {
        offsetX = popupBounds.right - mapBounds.right + 10;
      }

      if (offsetX !== 0 || offsetY !== 0) {
        this.map.panBy([
          offsetX,
          offsetY
        ])
      }
    }
  }

  findFeature(featureId) {
    let foundFeature = null;
    let stringFeatureId = featureId;

    if (typeof stringFeatureId !== 'string') {
      stringFeatureId = `${stringFeatureId}`;
    }

    if (this.props.geoJsonPoints) {
      for (let i = 0; i < this.props.geoJsonPoints.features.length; i++) {
        const feature = this.props.geoJsonPoints.features[i];
        let stringId = feature.properties.id;

        if (typeof stringId !== 'undefined' && stringId !== null) {
          if (typeof stringId !== 'string') {
            stringId = `${stringId}`;
          }

          if (stringId === stringFeatureId) {
            foundFeature = feature;
            break;
          }
        }
      }
    }

    if (this.props.geoJsonLines) {
      try {
        for (let i = 0; i < this.props.geoJsonLines.features.length; i++) {
          const feature = this.props.geoJsonLines.features[i];
          let stringId = feature.properties.id;

          if (typeof stringId !== 'undefined' && stringId !== null) {
            if (typeof stringId !== 'string') {
              stringId = `${stringId}`;
            }

            if (stringId === stringFeatureId) {
              foundFeature = feature;
              break;
            }
          }
        }
      }
      catch(e) {
        // Bad lines, abort.
      }
    }

    if (this.props.geoJsonPolygons) {
      try {
        for (let i = 0; i < this.props.geoJsonPolygons.features.length; i++) {
          const feature = this.props.geoJsonPolygons.features[i];
          let stringId = feature.properties.id;

          if (typeof stringId !== 'undefined' && stringId !== null) {
            if (typeof stringId !== 'string') {
              stringId = `${stringId}`;
            }

            if (stringId === stringFeatureId) {
              foundFeature = feature;
              break;
            }
          }
        }
      }
      catch(e) {
        // Bad polygons, abort.
      }
    }

    return foundFeature;
  }

  processLines(geoJson) {
    /*
    "River"
    */
    const processedGeoJson = Object.assign({}, geoJson);

    processedGeoJson.features = [];

    geoJson.features.forEach((feature) => {
      const processed = Object.assign({}, feature);

      if (!processed.properties.color) {
        let color = '#345d96'; // Ensure default color is set
        const type = processed.properties.type;

        if (type) {
          switch (type) {
            case 'River':
              color = '#345d96';
              break;
            default:
              break;
          }
        }

        processed.properties.color = color;
      }

      processedGeoJson.features.push(processed);
    });

    return processedGeoJson;
  }

  processPoints(geoJson) {
    /*
    "Airport" -> 'poi_pin'
    "Boat Launch" -> 'poi_pin'
    "Campground" -> 'campground_pin'
    "Campsite" -> 'campground_pin'
    "Entrance" -> 'poi_pin'
    "Gateway" -> 'gateway_pin'
    "Permit Office" -> 'permit_pin'
    "Picnic Area" -> 'dayUse_pin'
    "Ranger Station" -> 'poi_pin'
    "Recreation Area" -> 'recArea_pin'
    "Restroom" -> 'restroom_pin'
    "River Feature" -> 'poi_pin'
    "Trailhead" -> 'trailhead_pin'
    "Ticket" -> 'tour_pin'
    "Tour" -> 'tour_pin'
    "Visitor Center" -> 'poi_pin'
    "Water Source" -> 'drinking_water_pin'
    */

    const processedGeoJson = Object.assign({}, geoJson);

    processedGeoJson.features = [];

    geoJson.features.forEach((feature) => {
      const processed = Object.assign({}, feature);

      if (!processed.properties.symbol) {
        let symbol = 'poi_pin'; // Default to 'poi_pin'
        const type = processed.properties.type;

        if (type) {
          switch (type) {
            case 'Campground':
              symbol = 'camping_pin';
              break;
            case 'Campsite':
              symbol = 'camping_pin';
              break;
            case 'Gateway':
              symbol = 'gateway_pin';
              break;
            case 'Permit Office':
              symbol = 'permit_pin';
              break;
            case 'Picnic Area':
              symbol = 'dayUse_pin';
              break;
            case 'Recreation Area':
              symbol = 'recArea_pin';
              break;
            case 'Restroom':
              symbol = 'restroom_pin';
              break;
            case 'Trailhead':
              symbol = 'trailhead_pin';
              break;
            case 'Ticket':
            case 'Tour':
              symbol = 'tour_pin';
              break;
            case 'Water Source':
              symbol = 'drinking_water_pin';
              break;
            default:
              break;
          }
        }

        processed.properties.symbol = symbol;
      }

      processedGeoJson.features.push(processed);
    });

    return processedGeoJson;
  }

  processPolygons(geoJson) {
    const processedGeoJson = Object.assign({}, geoJson);

    processedGeoJson.features = [];

    geoJson.features.forEach((feature) => {
      const processed = Object.assign({}, feature);

      if (!processed.properties.color) {
        processed.properties.color = '#fee4a0'; // Ensure default color is set
      }

      if (!processed.properties.fillOpacity) {
        processed.properties.fillOpacity = 0.33; // Ensure default fill-opacity is set
      }

      // Don't add polygons to the processed data souce if they are flagged as non-visible
      if (!(processed.properties.visibility && processed.properties.visibility === "none")) {
        processedGeoJson.features.push(processed);
      }
    });

    return processedGeoJson;
  }

  showPopup(lngLat, geometryCoordinates, name) {
    const btm = lngLat ? [
      0,
      0
    ] : [
      0,
      -75/2
    ];
    const coordinates = lngLat ? [
      lngLat.lng,
      lngLat.lat
    ] : geometryCoordinates;

    if (this.popup) {
      this.popup.remove();
    }

    this.popup = new this.mapboxgl.Popup({
      maxWidth: 'none',
      anchor: 'bottom',
      closeButton: false,
      offset: btm
    });

    this.popup
      .setHTML(`
         <div class="map-popup">
           <div class="map-popup-message">
             <div class="map-popup-title">${name}</div>
           </div>
         </div>
      `)
      .setLngLat(coordinates)
      .addTo(this.map);
    this.checkPopupAndRepositionMap();
  }

  render() {
    // TODO: Show loading if permit.latitude and permit.longitude are not yet available
    return (mapboxgl.supported() ? null : <NoMapboxGlNotice />);
  }
}

PermitMap.propTypes = propTypes;
PermitMap.defaultProps = defaultProps;
